import {
  EyeOutlined,
  MailOutlined,
  MoreOutlined,
  PhoneOutlined,
  UserOutlined,
  UserSwitchOutlined
} from '@ant-design/icons';
import {
  App,
  Avatar,
  Button,
  Card,
  Col,
  Dropdown,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography
} from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { ApiUser } from '../../types';

const { Search } = Input;
const { Option } = Select;
const { Title } = Typography;

const AssignLeadModal = ({ visible, onCancel, lead, onAssignSubmit, message }: any) => {
  const [form] = Form.useForm();
  const [users, setUsers] = useState<ApiUser[]>([]);
  const [loading, setLoading] = useState(false);

  const title = `Assign Lead: ${lead?.full_name || ''}`;

  useEffect(() => {
    if (visible && lead) {
      form.resetFields();
      setLoading(true);
      api.get(`leads/users`, { params: { property_area: lead.property_area?.id } })
        .then(response => {
          setUsers(response.data.data || []);
        })
        .catch(error => {
          console.error('Failed to fetch users:', error);
          message.error('Gagal memuat data pengguna untuk area properti ini.');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [lead, visible, form, message]);

  const handleOk = () => {
    form.validateFields()
      .then(values => {
        onAssignSubmit(lead.id, values.assigned_to_id);
        form.resetFields();
      })
      .catch(info => {
        console.log('Validate Failed:', info);
      });
  };

  return (
    <Modal
      title={title}
      visible={visible}
      onOk={handleOk}
      onCancel={onCancel}
      confirmLoading={loading}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="assigned_to_id"
          label="Assign kepada"
          rules={[{ required: true, message: 'Silakan pilih pengguna!' }]}
        >
          <Select
            showSearch
            placeholder="Pilih sales agent"
            loading={loading}
            optionFilterProp="children"
            filterOption={(input, option: any) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            {users.map(user => (
              <Option key={user.id} value={user.id}>{user.full_name}</Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

const UnassignedLeadsList = () => {
  const [leads, setLeads] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    status: '',
    StatusFU: '',
    assigned_to: '',
    search: '',
    property_area_id: '',
    source: '',
    provinsi_id: '',
  });
  const [leadStatuses, setLeadStatuses] = useState<any[]>([]);
  const [leadFollowUpStatuses, setLeadFollowUpStatuses] = useState<any[]>([]);
  const [propertyAreas, setPropertyAreas] = useState<any[]>([]);
  const [reassignModalVisible, setReassignModalVisible] = useState(false);
  const [selectedLead, setSelectedLead] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debounceTimeout = React.useRef<NodeJS.Timeout | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = useAuth();
  const { message } = App.useApp();

  const shouldShowPropertyAreaFilter = hasPermission('lead.property');

  const { current, pageSize } = pagination;

  const fetchLeads = useCallback(async (page = 1, size = 10, currentFilters = filters) => {
    setLoading(true);
    try {
      const params: { [key: string]: any } = {
        page: current,
        limit: pageSize,
        ...filters,
      };

      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });

      const response = await api.get('leads/unassign', { params });
      const { data, meta } = response.data;

      setLeads(data);
      setPagination(prev => ({
        ...prev,
        total: meta.total,
      }));
    } catch (error: any) {
      console.error('Failed to fetch leads:', error);
      message.error('Gagal memuat data leads');
    } finally {
      setLoading(false);
    }
  }, [current, pageSize, filters, message]);

  useEffect(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(() => {
      handleFilterChange('search', searchTerm);
    }, 500);

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [searchTerm]);

  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        const requests = [
          api.get('lead-status'),
          api.get('lead-follow-up-status'),
        ];

        if (shouldShowPropertyAreaFilter) {
          requests.push(api.get('property-areas/role'));
        }

        const [statusesRes, followUpStatusesRes, propertyAreasRes] = await Promise.all(requests);

        setLeadStatuses(statusesRes.data.data.filter((item: any) => item.is_active));
        setLeadFollowUpStatuses(followUpStatusesRes.data.data.filter((item: any) => item.is_active));

        if (shouldShowPropertyAreaFilter && propertyAreasRes) {
          setPropertyAreas((propertyAreasRes.data.data || []).filter((item: any) => item.is_active));
        }

      } catch (error) {
        console.error('Failed to fetch filter data:', error);
        message.error('Gagal memuat data untuk filter');
      }
    };

    fetchFilterData();
  }, [shouldShowPropertyAreaFilter, message]);

  useEffect(() => {
    let stateUpdate = {};
    if (location.state?.statusId) {
      stateUpdate = { ...stateUpdate, status: location.state.statusId };
    }
    if (location.state?.leadSourceId) {
      stateUpdate = { ...stateUpdate, source: location.state.leadSourceId };
    }
    if (location.state?.provinsiId) {
      stateUpdate = { ...stateUpdate, provinsi_id: location.state.provinsiId };
    }
    if (location.state?.property_area_id) {
      stateUpdate = { ...stateUpdate, property_area_id: location.state.property_area_id };
    }
    if (location.state?.StatusFU) {
      stateUpdate = { ...stateUpdate, StatusFU: location.state.StatusFU };
    }

    if (Object.keys(stateUpdate).length > 0) {
      setFilters(prev => ({ ...prev, ...stateUpdate }));
      navigate('.', { replace: true, state: {} });
    } else {
      fetchLeads();
    }
  }, [location.state, navigate, fetchLeads]);

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    setPagination(pagination);
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleReassign = (lead: any) => {
    setSelectedLead(lead);
    setReassignModalVisible(true);
  };

  const handleAssignSubmit = async (leadId: string, assignedToId: string) => {
    try {
      await api.post(`leads/${leadId}/assign`, { assigned_to_id: assignedToId });
      message.success('Lead berhasil di-assign');
      setReassignModalVisible(false);
      fetchLeads();
    } catch (error: any) {
      console.error('Failed to assign lead:', error);
      message.error(error.response?.data?.message || 'Gagal meng-assign lead');
    }
  };

  const actionMenuItems = (record: any): any[] => {
    const items: any[] = [];

    if (hasPermission('lead.view')) {
      items.push({
        key: 'view',
        icon: <EyeOutlined />,
        label: 'Detail',
        onClick: () => navigate(`/leads/unassigned/${record.id}`),
      });
    }

    if (hasPermission('lead.assign')) {
      items.push({
        key: 'assign',
        icon: <UserSwitchOutlined />,
        label: 'Assign To',
        onClick: () => handleReassign(record),
      });
    }

    return items;
  };

  const columns: any[] = [
    {
      title: 'Lead Info',
      key: 'lead_info',
      width: 300,
      render: (_: any, record: any) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
            <Avatar size="small" icon={<UserOutlined />} style={{ marginRight: 8 }} />
            <strong>{record.full_name}</strong>
          </div>
          <div style={{ fontSize: 12, color: '#666' }}>
            {record.email && (
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                <MailOutlined style={{ marginRight: 4 }} />
                {record.email}
              </div>
            )}
            {record.phone && (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <PhoneOutlined style={{ marginRight: 4 }} />
                {record.phone}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'Sumber',
      dataIndex: ['lead_source', 'name'],
      key: 'lead_source',
      width: 180,
      render: (text: string) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: 'Tipe Properti',
      dataIndex: ['property_type', 'name'],
      key: 'property_type',
      render: (text: string) => <Tag>{text}</Tag>,
    },
    {
      title: 'Property Area',
      dataIndex: ['property_area', 'name'],
      key: 'property_area',
      render: (text: string) => text || '-',
    },
    {
      title: 'Tahapan',
      dataIndex: ['lead_status', 'name'],
      key: 'tahapan',
      render: (status: string) => <Tag>{status?.toUpperCase() || 'N/A'}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: ['lead_follow_up_status', 'name'],
      key: 'status',
      render: (status: string) => {
        if (!status) return <Tag>-</Tag>;
        const statusName = status.toLowerCase();
        let color = 'default';
        if (statusName === 'hot') color = 'error';
        else if (statusName === 'rsv') color = 'green';
        else if (statusName === 'cold') color = 'processing';
        else if (statusName === "o k") color = 'success';
        return <Tag color={color}>{status.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Status Ketertarikan',
      dataIndex: 'interest_status',
      key: 'interest_status',
      render: (interestStatus: string) => {
        if (!interestStatus) return <Tag>-</Tag>;

        const formatToTitleCase = (str: string) => {
          if (!str) return '';
          return str
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
        };

        const colorMap: { [key: string]: string } = {
          minat_pesan: 'green',
          minat_proses: 'processing',
          minat_tidak_pesan: 'warning',
          tidak_minat: 'error',
        };

        const color = colorMap[interestStatus] || 'default';
        const displayText = formatToTitleCase(interestStatus);

        return <Tag color={color}>{displayText}</Tag>;
      },
    },
    {
      title: 'Assigned To',
      dataIndex: ['assigned_to', 'full_name'],
      key: 'assigned_to',
      render: (text: string) => text || '-',
    },
    {
      title: 'Aksi',
      key: 'action',
      fixed: 'right',
      width: 120,
      render: (_: any, record: any) => {
        const menuItems = actionMenuItems(record);

        return menuItems.length > 0 ? (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        ) : null;
      },
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            Unassigned Leads
          </Title>
        </Col>
        <Col>
          <Space>
            <Button
              onClick={() => {
                setFilters({
                  status: '',
                  StatusFU: '',
                  assigned_to: '',
                  search: '',
                  property_area_id: '',
                  source: '',
                  provinsi_id: '',
                });
                setSearchTerm('');
              }}
            >
              Reset Filter
            </Button>
          </Space>
        </Col>
      </Row>

      <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} md={6}>
            <Search
              placeholder="Cari leads..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Tahapan"
              style={{ width: '100%' }}
              allowClear
              onChange={(value) => handleFilterChange('status', value)}
              value={filters.status || undefined}
            >
              {leadStatuses.map(status => (
                <Option key={status.id} value={status.id}>{status.name}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Status Follow Up"
              style={{ width: '100%' }}
              allowClear
              onChange={(value) => handleFilterChange('StatusFU', value)}
              value={filters.StatusFU || undefined}
            >
              {leadFollowUpStatuses.map(status => (
                <Option key={status.id} value={status.id}>{status.name}</Option>
              ))}
            </Select>
          </Col>
          {shouldShowPropertyAreaFilter && (
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Property Area"
                style={{ width: '100%' }}
                allowClear
                onChange={(value) => handleFilterChange('property_area_id', value)}
                value={filters.property_area_id || undefined}
              >
                {propertyAreas.map(area => (
                  <Option key={area.id} value={area.id}>{area.name}</Option>
                ))}
              </Select>
            </Col>
          )}
        </Row>

        <Table
          columns={columns}
          dataSource={leads}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} dari ${total} leads`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1500 }}
        />
      </Card>
      <AssignLeadModal
        visible={reassignModalVisible}
        onCancel={() => setReassignModalVisible(false)}
        lead={selectedLead}
        onAssignSubmit={handleAssignSubmit}
        message={message}
      />
    </div>
  );
};

export default UnassignedLeadsList;